

























import { Vue, Component } from 'vue-property-decorator';

import SectionData from '@/componentsV2/organisms/SectionData/SectionData.vue';
import BaseChart from '@/componentsV2/molecules/Chart/BaseChart.vue';

import BusinessWalletService from '@/api/BusinessWalletService';

import { IPeriod } from '@/models/period';
import { IChartSerie } from '@/models/performance';

import PerformanceUtils, { DatePickerShortcut } from '@/utils/PerformanceUtils';
import { ApexOptions } from 'apexcharts';

interface ICoinsReportsBreakdown {
  purchasedCoins: string | number;
  bonusCoins: string | number;
  twismCoins: string | number;
  value: string | number;
}

@Component({
  components: {
    SectionData,
    BaseChart
  }
})
export default class SellCoinsSoldChart extends Vue {
  businessWalletService = new BusinessWalletService();
  loading = false;
  period: IPeriod = PerformanceUtils.defaultPeriod();
  series: IChartSerie[] = [];

  get queryParameters() {
    return {
      from: this.period.start,
      to: this.period.end,
      ...(this.period.shortcut && {
        slug:
          PerformanceUtils.datepickerShortcutMapper[
            this.period.shortcut as DatePickerShortcut
          ]
      })
    };
  }

  get options(): ApexOptions {
    return {
      ...PerformanceUtils.chartOptions({
        colors: ['#78C60A']
      }),
      dataLabels: {
        enabled: false
      },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const breakdown: ICoinsReportsBreakdown = (this.series[0].data[
            dataPointIndex
          ][2] as unknown) as ICoinsReportsBreakdown;

          const date = new Date(this.series[0].data[dataPointIndex][0]);

          const formattedStartDate = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });

          const bonusIcon = (colorBackground: string, color: string) =>
            `<svg width="20" height="20" style="vertical-align:middle;margin-right:4px;" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="20" height="20" rx="10" fill="${colorBackground}"/><path d="M6.78613 13.2148L13.2138 6.78714" stroke="${color}" stroke-width="1.4" stroke-linecap="round"/><circle cx="6.99517" cy="6.46484" r="0.375" transform="rotate(-45 6.99517 6.46484)" fill="${color}" stroke="${color}" stroke-width="1.4"/><circle cx="12.9952" cy="13.2148" r="0.375" transform="rotate(-45 12.9952 13.2148)" fill="${color}" stroke="${color}" stroke-width="1.4"/></svg>`;
          const twismIcon = (colorBackground: string, color: string) =>
            `<svg width="20" height="20" style="vertical-align:middle;margin-right:4px;" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><rect y="0.5" width="20" height="20" rx="10" fill="${colorBackground}"/><path d="M14.5 8.25L10.6136 11.8125L8.56818 9.9375L5.5 12.75M14.5 8.25H12.0455M14.5 8.25V10.5" stroke="${color}" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
          const coinIcon = (color: string) =>
            `<svg width="12" height="13" style="vertical-align:middle;margin-left:4px;" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_5800_7325)"><path d="M1.5 3.64205V6.49951M1.5 6.49951C1.5 7.68321 3.5145 8.6426 6 8.6426C8.4855 8.6426 10.5 7.68321 10.5 6.49951M1.5 6.49951V9.3569C1.5 10.5406 3.5145 11.5 6 11.5C8.4855 11.5 10.5 10.5406 10.5 9.3569V6.49951M10.5 6.49951V3.64205M6 5.78619C8.48528 5.78619 10.5 4.82669 10.5 3.6431C10.5 2.4595 8.48528 1.5 6 1.5C3.51472 1.5 1.5 2.4595 1.5 3.6431C1.5 4.82669 3.51472 5.78619 6 5.78619Z" stroke="${color}" stroke-width="1.2"/></g><defs><clipPath id="clip0_5800_7325"><rect width="12" height="12" fill="white" transform="translate(0 0.5)"/></clipPath></defs></svg>`;
          const num = (n: string | number) =>
            n.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });
          return `
              <div style="background: #fff; border-radius: 16px; box-shadow: 0 4px 24px rgba(31, 41, 55, 0.08); padding: 24px 16px; width: 360px; font-family: inherit;">
                <div style="margin-bottom: 16px;">
                  <span style="color: #19191F; text-align: right; font-family: Inter; font-size: 14px; font-style: normal; font-weight: 600; line-height: normal;  ">${formattedStartDate}</span>
                </div>
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 7px; font-size: 14px; color: #10222D;">
                  <span>Coins purchased</span>
                  <span style="font-weight: 700; color: #10222D; display: flex; align-items: center;">${num(
                    breakdown.purchasedCoins || 0
                  )} ${coinIcon('#10222D')}</span>
                </div>
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 7px; font-size: 14px; color: ${
                  Number(breakdown.bonusCoins) ? '#10222D' : '#8D949E'
                }">
                  <span>Your reward bonus</span>
                  <span style="font-weight: 700; color: ${
                    Number(breakdown.bonusCoins) ? '#105A40' : '#8D949E'
                  }; display: flex; align-items: center;">
                    ${bonusIcon(
                      Number(breakdown.bonusCoins) ? '#D0F9E6' : '#F6F7FE',
                      Number(breakdown.bonusCoins) ? '#105A40' : '#8D949E'
                    )} +${num(breakdown.bonusCoins || 0)} ${coinIcon(
            Number(breakdown.bonusCoins) ? '#105A40' : '#8D949E'
          )}</span>
                </div>
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; font-size: 14px; color: ${
                  Number(breakdown.twismCoins) ? '#10222D' : '#8D949E'
                }">
                  <span>Twism's coins added</span>
                  <span style="font-weight: 700; color: ${
                    Number(breakdown.twismCoins) ? '#105A40' : '#8D949E'
                  }; display: flex; align-items: center;">
                    ${twismIcon(
                      Number(breakdown.twismCoins) ? '#D0F9E6' : '#F6F7FE',
                      Number(breakdown.twismCoins) ? '#105A40' : '#8D949E'
                    )} +${num(breakdown.twismCoins || 0)} ${coinIcon(
            Number(breakdown.twismCoins) ? '#105A40' : '#8D949E'
          )}</span>
                </div>
                <div style="border-top: 1px solid #E6E8EC; margin: 12px 0;"></div>
                <div style="display: flex; align-items: center; justify-content: space-between; font-size: 14px; font-weight: bold; color: #10222D;">
                  <span>Total amount of coins</span>
                  <span style="display: flex; align-items: center;">${num(
                    breakdown.value || 0
                  )} ${coinIcon('#10222D')}</span>
                </div>
              </div>
            `;
        }
      }
    };
  }

  created() {
    this.getCoinsReports();
  }

  async getCoinsReports({ useFilters = true } = {}) {
    this.loading = true;

    const queryParameters = useFilters ? this.queryParameters : {};

    const response = await this.businessWalletService.getYourCoinsSold(
      queryParameters
    );

    this.loading = false;

    if (response.status !== 200) return;

    this.series = PerformanceUtils.normalizedChartSeries({
      ...response.data,
      datasets: [response.data.datasets]
    });
  }

  handlePeriodChange(period: IPeriod) {
    this.period = PerformanceUtils.normalizedPeriod(period);
    this.getCoinsReports();
  }
}
